import { useEffect, useState } from "react";
import feesAndPaymentApis from "../../api/feesAndPaymentApis";
import {
  Invoice,
  InvoiceItemPayload,
  InvoiceStatus,
} from "../../types/feesAndPaymentType";
import ModalLayout from "../layouts/ModalLayout";
import CustomInput from "../CustomInput";
import CustomButton from "../CustomButton";
import { toast } from "sonner";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { fetchInvoices } from "../../features/feesAndPayment/FeesAndPaymentSlice";
import InvoiceItemsList from "../../pages/fees-and-payment/InvoiceItemList";
import { useBranchCode } from "../../util/hooks/useBranch";
import InvoiceItemsTable from "../invoice/InvoiceItemsTable";
import InvoiceTotalsSection from "../invoice/InvoiceTotalsSection";
import { useCreateInvoice } from "../../util/hooks/useInvoice";
import SubButton from "../SubButtons";

interface Props {
  onClose?: () => void;
  mode?: "CREATE" | "VIEW";
  template?: Invoice;
}

export default function CreateTemplate({ onClose, mode, template }: Props) {
  const [templateTitle, setTemplateTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { rows, totals, addRow, updateValue, setRows, calculateTotals } =
    useCreateInvoice();

  const dispatch = useDispatch<AppDispatch>();
  const { invoices, page, size } = useSelector(
    (state: RootState) => state.fees,
  );
  const branchCode = useBranchCode();
  const schoolCode = useSelector((state: RootState) => state.access.school);

  useEffect(() => {
    dispatch(
      fetchInvoices({
        branchCode,
        schoolCode: schoolCode.schoolCode,
        page,
        size,
      }),
    );
  }, [dispatch, branchCode, schoolCode.schoolCode, page, size]);

  const filteredInvoices = invoices.filter(
    (inv) => inv.paymentStatus === "DRAFT",
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateTitle.trim() || rows.length === 0) {
      toast.error("Please provide a title and at least one item");
      return;
    }

    setIsSubmitting(true);
    try {
      const invoiceItems: InvoiceItemPayload[] = rows.map((row) => {
        const baseAmount = Number(row.amount);
        const discount = Number(row.discount || 0);
        const tax = parseFloat(row.gst.replace("%", "") || "0");
        let discountedAmount =
          row.discountType === "%"
            ? baseAmount - (baseAmount * discount) / 100
            : baseAmount - discount;
        const taxAmount = discountedAmount * (tax / 100);

        return {
          itemTitle: row.itemName,
          baseAmount,
          discount,
          discountType: row.discountType === "%" ? "PERCENTAGE" : "FLAT",
          discountRemarks: row.discountRemark,
          tax,
          taxAmount,
          finalAmount: discountedAmount + taxAmount,
        };
      });

      await feesAndPaymentApis.createInvoice(
        {
          paymentTitle: templateTitle,
          amount: totals.grandTotal,
          invoiceItems,
          branchCode,
          dueDate: new Date().toISOString(),
          paymentCycle: null,
          recurringFrom: null,
          recurringTo: null,
          occurrences: null,
          toClassIds: [],
          toAcademicCycleId: [],
          toStudentIds: [],
        },
        "DRAFT" as InvoiceStatus,
      );

      dispatch(
        fetchInvoices({
          branchCode,
          schoolCode: schoolCode.schoolCode,
          page,
          size,
        }),
      );

      toast.success("Template created successfully");
      onClose?.();
    } catch (error) {
      toast.error("Failed to create template");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (mode === "VIEW" && template) {
      setTemplateTitle(template.paymentTitle);

      setRows(
        template.invoiceItems.map((item) => ({
          id: "1",
          itemName: item.itemTitle,
          amount: item.baseAmount.toString(),
          discount: item.discount.toString(),
          discountType: item.discountType === "PERCENTAGE" ? "%" : "₹",
          discountRemark: item.discountRemarks || "",
          gst: `${item.tax}%`,
        })),
      );
    }
  }, [mode, template]);

  const hasInvalidAmount = rows.some(
    (row) => !row.amount || Number(row.amount) <= 0,
  );

  return (
    <ModalLayout onClose={onClose} width="max-w-6xl">
      <div className="flex flex-col gap-4">
        <h4 className="text-xl font-semibold text-neutral-400">
          {mode === "VIEW" ? "View" : "New Template"}
        </h4>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col max-h-[300px] overflow-y-auto custom-scrollbar px-2 gap-4"
        >
          <CustomInput
            name="templateTitle"
            label="Template Title"
            placeholder="Daycare fee / Admission fee"
            value={templateTitle}
            onChange={(e) => setTemplateTitle(e.target.value)}
            required
          />

          {rows.length > 0 && mode !== "VIEW" && (
            <div
              className="max-h-[800px] 
            "
            >
              <InvoiceItemsTable
                rows={rows}
                updateValue={updateValue}
                addRow={addRow}
                setRows={setRows}
                calculateTotals={calculateTotals}
              />
            </div>
          )}

          {filteredInvoices.map((invoice) => (
            <div key={invoice.invoiceId} className="border rounded p-1">
              <InvoiceItemsList invoice={invoice} />
            </div>
          ))}
          <div className="sticky bottom-0 bg-white border-t pt-4 items-center flex flex-col md:flex-row justify-between gap-4">
            {rows.length === 0 && mode !== "VIEW" && (
              <div className="flex gap-2 items-center justify-end">
                <label className="text-sm font-semibold text-neutral-400 text-nowrap">
                  To create Template
                </label>
                <SubButton
                  text="Add Items"
                  color="bg-success-600"
                  onClick={addRow}
                  type="button"
                />
              </div>
            )}
            {rows.length > 0 && mode !== "VIEW" && (
              <InvoiceTotalsSection totals={totals} />
            )}

            <div className="flex gap-3 justify-end">
              <CustomButton
                text="Close"
                color="bg-neutral-200"
                onClick={onClose}
                type="button"
              />
              {mode !== "VIEW" && (
                <CustomButton
                  text={isSubmitting ? "Creating..." : "Create Template"}
                  color="bg-primary-500"
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !templateTitle ||
                    rows.length === 0 ||
                    hasInvalidAmount
                  }
                />
              )}
            </div>
          </div>
        </form>
      </div>
    </ModalLayout>
  );
}
